import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "components/card";
import Checkbox from "components/checkbox";
import { staffAPI } from "services/staffService";

/**
 * Module and action definitions for permission matrix
 */
const PERMISSION_MODULES = [
  { key: "dashboard", label: "Dashboard" },
  { key: "lead_management", label: "Lead Management" },
  { key: "lead_folder", label: "Lead Folder" },
  { key: "client_management", label: "Client Management" },
  { key: "client_folder", label: "Client Folder" },
  { key: "careof_management", label: "CareOf Management" },
  { key: "calendar", label: "Calendar" },
  { key: "staff_management", label: "Staff Management" },
];

const ACTIONS = [
  { key: "read", label: "Read" },
  { key: "create", label: "Create" },
  { key: "update", label: "Update" },
  { key: "delete", label: "Delete" },
];

const PermissionMatrix = () => {
  const navigate = useNavigate();
  const { staffId } = useParams();

  const [staffName, setStaffName] = useState("");
  const [permissions, setPermissions] = useState({});
  const [originalPermissions, setOriginalPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  // Fetch staff details and permissions
  useEffect(() => {
    const fetchData = async () => {
      try {
        const staffResponse = await staffAPI.getStaffById(staffId);
        const staff = staffResponse?.data || staffResponse;
        setStaffName(staff.name || "");

        const permResponse = await staffAPI.getStaffPermissions(staffId);
        const perms = permResponse?.data?.permissions || permResponse?.permissions || {};
        
        // Ensure all modules exist with all actions
        const normalizedPerms = {};
        PERMISSION_MODULES.forEach((module) => {
          normalizedPerms[module.key] = {
            read: perms?.[module.key]?.read || false,
            create: perms?.[module.key]?.create || false,
            update: perms?.[module.key]?.update || false,
            delete: perms?.[module.key]?.delete || false,
          };
        });

        setPermissions(normalizedPerms);
        setOriginalPermissions(JSON.parse(JSON.stringify(normalizedPerms)));
      } catch (err) {
        const message = err?.response?.data?.message || "Failed to load permissions";
        showToast(message, "error");
        setTimeout(() => navigate("/admin/staff-management"), 2000);
      } finally {
        setFetching(false);
      }
    };

    fetchData();
  }, [staffId, navigate]);

  const handlePermissionChange = (module, action) => {
    setPermissions((prev) => ({
      ...prev,
      [module]: {
        ...prev[module],
        [action]: !prev[module][action],
      },
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await staffAPI.updateStaffPermissions(staffId, permissions);
      showToast("Permissions updated successfully", "success");
      setOriginalPermissions(JSON.parse(JSON.stringify(permissions)));
      setTimeout(() => {
        navigate("/admin/staff-management");
      }, 1500);
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to update permissions";
      showToast(message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/staff-management");
  };

  const handleReset = () => {
    setPermissions(JSON.parse(JSON.stringify(originalPermissions)));
  };

  const hasChanges = JSON.stringify(permissions) !== JSON.stringify(originalPermissions);

  if (fetching) {
    return (
      <div className="mt-3 flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
          <p className="text-gray-600 dark:text-gray-400">Loading permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 flex h-full w-full flex-col gap-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-navy-700 dark:text-white">Permission Management</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Manage permissions for: <span className="font-medium text-navy-700 dark:text-white">{staffName}</span>
        </p>
      </div>

      {/* Toast */}
      {toast.show && (
        <div
          className={`rounded-xl p-4 ${
            toast.type === "success"
              ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Info Card */}
      <Card extra="p-4 border-l-4 border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20">
        <p className="text-sm text-blue-700 dark:text-blue-400">
          ℹ️ Check the boxes to grant permissions. Unchecked boxes mean no permission. Changes are saved to the backend.
        </p>
      </Card>

      {/* Permission Matrix */}
      <Card extra="overflow-x-auto">
        <div className="w-full">
          {/* Table */}
          <div className="inline-block min-w-full">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-white/10">
                  <th className="px-6 py-4 text-left text-sm font-bold text-navy-700 dark:text-white">
                    Module
                  </th>
                  {ACTIONS.map((action) => (
                    <th
                      key={action.key}
                      className="px-6 py-4 text-center text-sm font-bold text-navy-700 dark:text-white"
                    >
                      {action.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PERMISSION_MODULES.map((module) => (
                  <tr
                    key={module.key}
                    className="border-b border-gray-200 hover:bg-gray-50 dark:border-white/10 dark:hover:bg-navy-700/50"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-navy-700 dark:text-white">
                      {module.label}
                    </td>
                    {ACTIONS.map((action) => (
                      <td key={`${module.key}-${action.key}`} className="px-6 py-4 justify-items-center">
                        <Checkbox
                          color="brand"
                          checked={permissions[module.key]?.[action.key] || false}
                          onChange={() => handlePermissionChange(module.key, action.key)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      {/* Legend */}
      <Card extra="p-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <h3 className="mb-2 font-medium text-navy-700 dark:text-white">Action Meanings</h3>
            <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <li><span className="font-medium">Read:</span> View/List data</li>
              <li><span className="font-medium">Create:</span> Add new records</li>
              <li><span className="font-medium">Update:</span> Edit existing records</li>
              <li><span className="font-medium">Delete:</span> Remove records</li>
            </ul>
          </div>
          <div>
            <h3 className="mb-2 font-medium text-navy-700 dark:text-white">Example</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              If "Lead Management" has Read ✓ and Create ✓, staff can view and create leads, but cannot edit or delete them.
            </p>
          </div>
        </div>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={handleCancel}
          disabled={loading}
          className="flex-1 rounded-xl border border-gray-200 py-3 font-medium text-navy-700 transition-all hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:text-white dark:hover:bg-navy-700 md:flex-none md:px-6"
        >
          Cancel
        </button>
        {hasChanges && (
          <button
            onClick={handleReset}
            disabled={loading}
            className="flex-1 rounded-xl border border-gray-400 py-3 font-medium text-gray-700 transition-all hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-navy-700 md:flex-none md:px-6"
          >
            Reset
          </button>
        )}
        <button
          onClick={handleSave}
          disabled={loading || !hasChanges}
          className="flex-1 rounded-xl bg-brand-500 py-3 font-medium text-white transition-all hover:bg-brand-600 disabled:opacity-50 dark:bg-brand-400 dark:hover:bg-brand-500 md:flex-none md:px-6"
        >
          {loading ? "Saving..." : "Save Permissions"}
        </button>
      </div>
    </div>
  );
};

export default PermissionMatrix;
